/**
 * CSRF-Token des Techniker-Portals.
 *
 * Der Server setzt das Cookie `portal_csrf` bewusst OHNE `httpOnly` — nur so
 * kann das Frontend seinen Wert lesen und bei schreibenden Anfragen als Header
 * `X-CSRF-Token` zurückspiegeln. Genau das kann eine fremde Seite nicht: Cookies
 * mitsenden lassen ja, lesen oder eigene Header setzen nein.
 *
 * Backend-Gegenstück: `backend/src/lib/portalCsrf.ts`.
 * Kundenportal-Gegenstück: `readCustomerCsrfCookie` in `lib/api.customer.ts`
 * (eigenes Cookie `customer_csrf`, die beiden Portale teilen sich keinen Token).
 */

export const PORTAL_CSRF_COOKIE = 'portal_csrf';
export const PORTAL_CSRF_HEADER = 'X-CSRF-Token';

/** Methoden ohne Seiteneffekt — der Server prüft dort nichts. */
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

const COOKIE_PATTERN = new RegExp(`(?:^|;\\s*)${PORTAL_CSRF_COOKIE}=([^;]*)`);

export function readPortalCsrfToken(): string | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(COOKIE_PATTERN);
  return m?.[1] ? decodeURIComponent(m[1]) : null;
}

export function isSafeMethod(method: string | undefined): boolean {
  return SAFE_METHODS.has((method ?? 'GET').toUpperCase());
}

/**
 * Ergänzt den CSRF-Header für schreibende Anfragen. Fehlt das Cookie (noch
 * nicht angemeldet, Cookie abgelaufen), wird nichts gesetzt — der Server
 * antwortet dann mit 403 und einem frischen Cookie, siehe `apiRequest`.
 */
export function withCsrfHeader(
  method: string | undefined,
  headers: Record<string, string> = {}
): Record<string, string> {
  if (isSafeMethod(method)) return headers;
  const token = readPortalCsrfToken();
  return token ? { ...headers, [PORTAL_CSRF_HEADER]: token } : headers;
}

/**
 * Fügt einer `RequestInit` den Header hinzu — für die Stellen, die aus
 * technischen Gründen (Blob-/Stream-Antworten) direkt `fetch` benutzen.
 */
export function withCsrf(options: RequestInit = {}): RequestInit {
  return {
    ...options,
    headers: withCsrfHeader(options.method, (options.headers as Record<string, string>) ?? {}),
  };
}
